import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileWarning } from 'lucide-react';
import { comprobanteService } from '../../services/comprobanteService';
import { useRegularizacion } from '../../context/RegularizacionContext';
import type { ComprobanteFaltante, EntidadDinero } from '../../types/comprobante';
import { PARAM_REEMPLAZAR } from '../../utils/comprobanteEntidad';
import { estaVencido, indicacionDeObjecion, textoTiempoRestante, vencimientoEnChile } from '../../utils/comprobantePlazo';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { AdjuntarComprobanteModal, type ItemParaAdjuntar } from './AdjuntarComprobanteModal';
import { Checkbox } from '../ui/Checkbox';

const ANCLA = 'comprobantes-pendientes';
const clave = (f: ComprobanteFaltante) => `${f.movimiento_id}:${f.respaldo}`;
const aItem = (f: ComprobanteFaltante): ItemParaAdjuntar => ({
  movimientoId: f.movimiento_id, respaldo: f.respaldo, descripcion: f.descripcion, monto: f.monto, fecha: f.fecha,
});

interface ComprobantesPendientesPanelProps {
  readonly entidad: EntidadDinero;
  /** Para recargar el listado de movimientos después de adjuntar. */
  readonly onCambio: () => void;
}

export const ComprobantesPendientesPanel: React.FC<ComprobantesPendientesPanelProps> = ({ entidad, onCambio }) => {
  const { recargar: recargarRegularizacion } = useRegularizacion();
  const location = useLocation();
  const navigate = useNavigate();
  const [faltantes, setFaltantes] = useState<ComprobanteFaltante[]>([]);
  const [ahora, setAhora] = useState(() => new Date());
  const [seleccion, setSeleccion] = useState<ReadonlySet<string>>(new Set());
  const [paraAdjuntar, setParaAdjuntar] = useState<{ items: readonly ItemParaAdjuntar[]; modo: 'adjuntar' | 'reemplazar' } | null>(null);
  const panelRef = useRef<HTMLElement>(null);

  const [recarga, setRecarga] = useState(0);
  const pideReemplazar = new URLSearchParams(location.search).has(PARAM_REEMPLAZAR);
  // La carga depende del tipo y el id, no del objeto: las pantallas arman la entidad en cada render.
  const { tipo: entidadTipo, id: entidadId } = entidad;

  useEffect(() => {
    let vigente = true;
    const cargar = async () => {
      try {
        const lista = await comprobanteService.getFaltantes({ tipo: entidadTipo, id: entidadId, nombre: '' });
        if (!vigente) return;
        setFaltantes(lista);
        setAhora(new Date());
        setSeleccion(new Set());
        // Llegada desde el botón "Reemplazar comprobante" de la campana: abre la carga del comprobante
        // objetado más antiguo y limpia el parámetro para que recargar la página no la vuelva a abrir (H23).
        if (pideReemplazar && lista.length > 0) {
          const masAntiguo = lista
            .filter((f) => f.objecion_motivo)
            .sort((a, b) => new Date(a.pendiente_desde).getTime() - new Date(b.pendiente_desde).getTime())[0];
          if (masAntiguo) setParaAdjuntar({ items: [aItem(masAntiguo)], modo: 'reemplazar' });
          navigate({ pathname: location.pathname, hash: location.hash }, { replace: true });
        }
      } catch (e: unknown) {
        if (vigente) reportarErrorInesperado(e, 'No pudimos cargar los comprobantes pendientes.');
      }
    };
    void cargar();
    return () => { vigente = false; };
  }, [entidadTipo, entidadId, recarga, pideReemplazar, location.pathname, location.hash, navigate]);

  useEffect(() => {
    if (faltantes.length > 0 && location.hash === `#${ANCLA}`) {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [faltantes.length, location.hash]);

  if (faltantes.length === 0) return null;

  const alternar = (k: string, marcado: boolean) => {
    const nueva = new Set(seleccion);
    if (marcado) nueva.add(k); else nueva.delete(k);
    setSeleccion(nueva);
  };

  const alAdjuntar = () => {
    setRecarga((n) => n + 1);
    onCambio();
    recargarRegularizacion();
  };

  return (
    <section id={ANCLA} ref={panelRef} className="bg-amber-50 border-2 border-amber-300 rounded-xl shadow-sm p-6 space-y-4 scroll-mt-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 p-2 rounded-lg"><FileWarning className="w-5 h-5 text-amber-600" aria-hidden="true" /></div>
          <div>
            <h3 className="text-lg font-bold text-amber-900">Comprobantes pendientes</h3>
            <p className="text-sm text-amber-700">
              {faltantes.length} {faltantes.length === 1 ? 'movimiento sin comprobante' : 'movimientos sin comprobante'}
            </p>
          </div>
        </div>
        <button
          type="button"
          disabled={seleccion.size === 0}
          onClick={() => { setParaAdjuntar({ items: faltantes.filter((f) => seleccion.has(clave(f))).map(aItem), modo: 'adjuntar' }); }}
          className="min-h-11 px-4 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          Adjuntar un comprobante a {seleccion.size || 'los'} seleccionados
        </button>
      </div>

      <ul className="divide-y divide-amber-200">
        {faltantes.map((f) => {
          const vencido = estaVencido(f.pendiente_desde, ahora);
          const k = clave(f);
          return (
            <li key={k} className="py-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-48">
                <Checkbox
                  id={`pendiente-${k}`}
                  label={`${formatDate(f.fecha)} · ${f.descripcion ?? 'Sin descripción'}${f.respaldo === 'reembolso' ? ' (reembolso)' : ''}`}
                  checked={seleccion.has(k)}
                  onChange={(marcado) => { alternar(k, marcado); }}
                />
                <p className="text-xs text-slate-600 ml-7">
                  {formatCurrency(Number(f.monto))} ·{' '}
                  <span title={`Vence el ${vencimientoEnChile(f.pendiente_desde)}`}
                    className={vencido ? 'font-semibold text-red-700' : 'text-amber-800'}>
                    {vencido ? 'Vencido' : `Pendiente · ${textoTiempoRestante(f.pendiente_desde, ahora) ?? ''}`}
                  </span>
                </p>
                {f.objecion_motivo && (
                  <p className="text-xs text-red-700 ml-7">{indicacionDeObjecion(f.objecion_motivo)}</p>
                )}
              </div>
              <button type="button"
                onClick={() => { setParaAdjuntar({ items: [aItem(f)], modo: f.objecion_motivo ? 'reemplazar' : 'adjuntar' }); }}
                className="min-h-11 px-3 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-semibold hover:bg-emerald-200">
                {f.objecion_motivo ? 'Reemplazar comprobante' : 'Adjuntar'}
              </button>
            </li>
          );
        })}
      </ul>

      {paraAdjuntar && (
        <AdjuntarComprobanteModal
          isOpen
          onClose={() => { setParaAdjuntar(null); }}
          onSuccess={alAdjuntar}
          entidad={entidad}
          items={paraAdjuntar.items}
          modo={paraAdjuntar.modo}
        />
      )}
    </section>
  );
};
